import { Grid } from '@material-ui/core'
import React, { useEffect } from 'react'
import { Control, FieldError, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ControlledTextField } from '../form/ControlledTextField'
import { validators } from '../../validation/validators'
import type { CostsDialogFormValues } from './CostsDialog'

type InternalFormValues = {
  absoluteOccurrenceYear: string | null
}

type Props = {
  control: Control<CostsDialogFormValues>
  defaultValue?: string
  occurrenceYearError?: FieldError
  buildingYear?: number
}

export function ControlledOccurenceYear({
  control: costsDialogControl,
  defaultValue,
  occurrenceYearError,
  buildingYear,
}: Props): React.ReactElement {
  const { t } = useTranslation()

  const defaultAbsoluteYear =
    buildingYear !== undefined
      ? String(buildingYear + Number(defaultValue ?? 0))
      : null

  const costsDialogSetValue = costsDialogControl.setValue

  const {
    control: internalControl,
    errors: internalErrors,
    setValue: internalSetValue,
    trigger: internalTrigger,
  } = useForm<InternalFormValues>({
    defaultValues: {
      absoluteOccurrenceYear: defaultAbsoluteYear,
    },
  })

  const onRelativeYearChange = (relativeYear: string) => {
    if (buildingYear === undefined || Number.isNaN(Number(relativeYear))) {
      return
    }
    const absoluteYear = String(Number(relativeYear) + buildingYear)
    internalSetValue('absoluteOccurrenceYear', absoluteYear, {
      shouldValidate: true,
    })
  }

  const onAbsoluteYearChange = async (absoluteYear: string) => {
    if (buildingYear === undefined || Number.isNaN(Number(absoluteYear))) {
      return
    }
    if (Number(absoluteYear) < buildingYear) {
      costsDialogSetValue('occurrenceYear', 0)
      return
    }
    const relativeYear = String(Number(absoluteYear) - buildingYear)
    costsDialogSetValue('occurrenceYear', relativeYear, {
      shouldValidate: true,
    })
    await internalTrigger('absoluteOccurrenceYear')
  }

  const getOccurrenceYearValue = () =>
    costsDialogControl.getValues('occurrenceYear')

  useEffect(() => {
    onRelativeYearChange(getOccurrenceYearValue())
    // This should only be triggered when the building year is updated.
    // Other cases are handled separately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingYear])

  return (
    <Grid container spacing={2}>
      <Grid item xs={buildingYear !== undefined ? 6 : 12}>
        <ControlledTextField
          control={costsDialogControl}
          name="occurrenceYear"
          error={occurrenceYearError}
          type="text"
          label={t('costs.common.occurrenceYear')}
          rules={{ ...validators.nonNegativeInteger }}
          onChange={(event) => onRelativeYearChange(event.target.value)}
        />
      </Grid>
      {buildingYear !== undefined && (
        <Grid item xs={6}>
          <ControlledTextField
            control={internalControl}
            name="absoluteOccurrenceYear"
            error={internalErrors.absoluteOccurrenceYear}
            type="text"
            label={t('costs.dialog.absoluteOccurrenceYear')}
            rules={{ ...validators.positiveInteger }}
            onChange={(event) => onAbsoluteYearChange(event.target.value)}
          />
        </Grid>
      )}
    </Grid>
  )
}
